import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import moment from 'moment';

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  FileText,
  Send,
  Eye,
  Trash2,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  Filter,
  MoreVertical,
  UserPlus,
  Users,
  Search,
  ArrowLeft,
  Receipt,
  Table2
} from "lucide-react";
import { ConvertEstimateDialog } from "@/components/contracts/ConvertEstimateDialog";
import { NewSheetDialog } from "@/components/production/NewSheetDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const statusColors = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  viewed: "bg-purple-100 text-purple-700",
  accepted: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  expired: "bg-orange-100 text-orange-700"
};

const statusIcons = {
  draft: FileText,
  sent: Send,
  viewed: Eye,
  accepted: CheckCircle2,
  rejected: XCircle,
  expired: Clock
};

export default function EstimatesPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [convertEstimateId, setConvertEstimateId] = useState(null);
  const [sheetEstimateId, setSheetEstimateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");

  // Create Estimate Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [qualifiedLeads, setQualifiedLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // New Client Modal state
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "", address: "", source: "manual", value: 0
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [estimatesRes, templatesRes] = await Promise.all([
        axios.get(`${API}/estimates`),
        axios.get(`${API}/estimate-templates`)
      ]);
      setEstimates(estimatesRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error("Failed to fetch estimates");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = async () => {
    setShowCreateModal(true);
    setSelectedLeadId(null);
    setSearchQuery("");
    setLoadingLeads(true);
    
    try {
      // Fetch leads with "qualified" status
      const response = await axios.get(`${API}/leads?status=qualified`);
      setQualifiedLeads(response.data || []);
    } catch (error) {
      // Fallback: fetch all leads if status filter fails
      try {
        const allLeadsRes = await axios.get(`${API}/leads`);
        // Filter for qualified status client-side
        const qualified = (allLeadsRes.data || []).filter(
          l => l.status === "qualified" || l.status === "Qualified"
        );
        setQualifiedLeads(qualified);
      } catch (err) {
        setQualifiedLeads([]);
      }
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeadId(leadId);
  };

  const handleCreateEstimate = () => {
    if (selectedLeadId) {
      navigate(`/estimates/new?lead_id=${selectedLeadId}`);
    }
    setShowCreateModal(false);
  };

  const handleCreateNewClient = () => {
    setShowCreateModal(false);
    setShowNewClientModal(true);
  };

  const handleSubmitNewClient = async () => {
    if (!newClient.first_name || !newClient.last_name || !newClient.email) {
      toast.error("First name, last name, and email are required");
      return;
    }
    setCreatingClient(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        ...newClient,
        status: "qualified"
      });
      const leadId = response.data.lead_id;
      toast.success("Client created! Opening estimate builder...");
      setShowNewClientModal(false);
      setNewClient({ first_name: "", last_name: "", email: "", phone: "", company: "", address: "", source: "manual", value: 0 });
      navigate(`/estimates/new?lead_id=${leadId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    } finally {
      setCreatingClient(false);
    }
  };

  const filteredQualifiedLeads = qualifiedLeads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${lead.first_name || ""} ${lead.last_name || ""}`.toLowerCase();
    const email = (lead.email || "").toLowerCase();
    const phone = (lead.phone || "").toLowerCase();
    return fullName.includes(query) || email.includes(query) || phone.includes(query);
  });

  const handleSendEstimate = async (estimateId) => {
    try {
      const response = await axios.post(`${API}/estimates/${estimateId}/send`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send estimate");
    }
  };

  const handleDeleteEstimate = async (estimateId) => {
    if (!window.confirm("Are you sure you want to delete this estimate?")) return;
    try {
      await axios.delete(`${API}/estimates/${estimateId}`);
      setEstimates(estimates.filter(e => e.estimate_id !== estimateId));
      toast.success("Estimate deleted");
    } catch (error) {
      toast.error("Failed to delete estimate");
    }
  };

  const filteredEstimates = estimates.filter(e => {
    if (activeTab !== "all" && e.status !== activeTab) return false;
    if (filterCategory !== "all" && e.category !== filterCategory) return false;
    return true;
  });

  const stats = {
    total: estimates.length,
    draft: estimates.filter(e => e.status === "draft").length,
    sent: estimates.filter(e => e.status === "sent").length,
    accepted: estimates.filter(e => e.status === "accepted").length,
    totalValue: estimates.reduce((sum, e) => sum + (e.total || 0), 0),
    acceptedValue: estimates.filter(e => e.status === "accepted").reduce((sum, e) => sum + (e.total || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="estimates-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estimates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Estimates
          </h1>
          <p className="text-slate-600 mt-1">
            {estimates.length} total • ${stats.totalValue.toLocaleString()} in estimates
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate("/estimates/templates")} variant="outline" data-testid="manage-templates-btn">
            <FileText className="w-4 h-4 mr-2" />
            Manage Templates
          </Button>
          <Button onClick={openCreateModal} data-testid="create-estimate-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Estimate
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Estimates</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold">{stats.draft + stats.sent}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Accepted</p>
                <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Accepted Value</p>
                <p className="text-2xl font-bold text-green-600">${stats.acceptedValue.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="draft">Drafts</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="accepted">Accepted</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-40">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="Cabinetry">Cabinetry</SelectItem>
            <SelectItem value="Remodel">Remodel</SelectItem>
            <SelectItem value="Refacing">Refacing</SelectItem>
            <SelectItem value="Refinishing">Refinishing</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Estimates List */}
      <div className="space-y-4" data-testid="estimates-list">
        {filteredEstimates.length > 0 ? (
          filteredEstimates.map(estimate => {
            const StatusIcon = statusIcons[estimate.status] || FileText;
            return (
              <Card 
                key={estimate.estimate_id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/estimates/${estimate.estimate_id}`)}
                data-testid={`estimate-card-${estimate.estimate_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <StatusIcon className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">
                            {estimate.estimate_number}
                          </h3>
                          <Badge className={statusColors[estimate.status]}>
                            {estimate.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600">
                          {estimate.customer_name} • {estimate.category}
                          {estimate.sub_category && ` - ${estimate.sub_category}`}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Created {moment(estimate.created_at).fromNow()}
                          {estimate.sent_at && ` • Sent ${moment(estimate.sent_at).fromNow()}`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">
                          ${estimate.total?.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {estimate.line_items?.length || 0} items
                        </p>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/estimates/${estimate.estimate_id}`);
                          }}>
                            <Eye className="w-4 h-4 mr-2" />
                            View / Edit
                          </DropdownMenuItem>
                          {estimate.status === "draft" && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleSendEstimate(estimate.estimate_id);
                            }}>
                              <Send className="w-4 h-4 mr-2" />
                              Send to Customer
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setConvertEstimateId(estimate.estimate_id); }} data-testid={`convert-estimate-${estimate.estimate_id}`}>
                            <Receipt className="w-4 h-4 mr-2" />
                            {estimate.status === "accepted" ? "Convert to Invoice / Contract" : "Create Invoice / Contract"}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSheetEstimateId(estimate.estimate_id); }} data-testid={`production-sheet-${estimate.estimate_id}`}>
                            <Table2 className="w-4 h-4 mr-2" />
                            Production Sheet
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEstimate(estimate.estimate_id);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No estimates found</p>
              <p className="text-sm text-slate-400 mt-1">
                Click &quot;Create Estimate&quot; to get started
              </p>
              <Button className="mt-4" onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                Create Estimate
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Estimate Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-indigo-600" />
              Create New Estimate
            </DialogTitle>
            <DialogDescription>
              Select a qualified lead to create an estimate for, or create a new client.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col space-y-4 py-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leads by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="lead-search-input"
              />
            </div>

            {/* Create New Client Option */}
            <button
              onClick={handleCreateNewClient}
              className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
              data-testid="create-new-client-btn"
            >
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Create New Client</p>
                <p className="text-sm text-slate-500">Add a new lead and create estimate</p>
              </div>
            </button>

            {/* Qualified Leads List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-slate-400" />
                <Label className="text-sm text-slate-600">
                  Qualified Leads ({filteredQualifiedLeads.length})
                </Label>
              </div>
              
              {loadingLeads ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-sm text-slate-500">Loading leads...</span>
                </div>
              ) : filteredQualifiedLeads.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredQualifiedLeads.map(lead => (
                    <button
                      key={lead.lead_id}
                      onClick={() => handleSelectLead(lead.lead_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                        selectedLeadId === lead.lead_id
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      data-testid={`lead-option-${lead.lead_id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                        selectedLeadId === lead.lead_id ? "bg-indigo-600" : "bg-slate-400"
                      }`}>
                        {(lead.first_name?.[0] || "").toUpperCase()}
                        {(lead.last_name?.[0] || "").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="text-sm text-slate-500 truncate">
                          {lead.email || lead.phone || "No contact info"}
                        </p>
                      </div>
                      {selectedLeadId === lead.lead_id && (
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">
                    {searchQuery ? "No leads match your search" : "No qualified leads found"}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Mark leads as &quot;Qualified&quot; in the Leads page to see them here
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEstimate} 
              disabled={!selectedLeadId}
              data-testid="confirm-create-estimate-btn"
            >
              Create Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Client Modal */}
      <Dialog open={showNewClientModal} onOpenChange={setShowNewClientModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-600" />
              Create New Client
            </DialogTitle>
            <DialogDescription>
              Add client details. An estimate will be created automatically after.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input value={newClient.first_name} onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })} placeholder="John" data-testid="nc-first-name" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={newClient.last_name} onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })} placeholder="Smith" data-testid="nc-last-name" />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="john@example.com" data-testid="nc-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} placeholder="905-555-1234" data-testid="nc-phone" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={newClient.company} onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} placeholder="Company name" data-testid="nc-company" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={newClient.address} onChange={(e) => setNewClient({ ...newClient, address: e.target.value })} placeholder="123 Main St, City" data-testid="nc-address" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewClientModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitNewClient} disabled={creatingClient} data-testid="nc-submit">
              {creatingClient ? "Creating..." : "Create & Start Estimate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConvertEstimateDialog open={!!convertEstimateId} onOpenChange={(o) => !o && setConvertEstimateId(null)} estimateId={convertEstimateId} />
      <NewSheetDialog open={!!sheetEstimateId} onOpenChange={(o) => !o && setSheetEstimateId(null)} estimateId={sheetEstimateId} />
    </div>
  );
}
